import React, { useRef } from "react";
import { NavLink, useMatch } from "react-router-dom";

import { useSidebar } from "./hooks/useSidebar";
import { MakulaIcon } from "../MakulaIcon";
import Tooltip from "../Tooltip";

interface SidebarMenuButtonProps {
  asChild?: boolean;
  /** When set, overrides route matching for active styling (NavLink + icon). */
  isActive?: boolean;
  icon?: React.ReactNode;
  activeIcon?: React.ReactNode;
  label?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  showTooltip?: boolean;
  /**
   * Render as if the sidebar were expanded (show the label, no tooltip) even
   * when the global sidebar state is collapsed. Used inside the collapsed rail's
   * hover flyout, which shows full labels despite the rail being collapsed.
   */
  forceExpanded?: boolean;
}

/**
 * SidebarMenuButton - Clickable menu button with icon and label
 *
 * @param asChild - If true, passes props to child element (for custom Link components)
 * @param isActive - Manual control of active state
 * @param icon - Icon element (inactive state)
 * @param activeIcon - Icon element for active state (optional, falls back to icon)
 * @param label - Button label text
 * @param to - React Router path (for internal navigation)
 * @param href - URL (for external links)
 * @param onClick - Click handler
 * @param className - Additional CSS classes
 * @param children - Custom content (when not using icon/label props)
 * @param showTooltip - Show tooltip on hover
 */
export function SidebarMenuButton({
  asChild = false,
  isActive,
  icon,
  activeIcon,
  label,
  to,
  href,
  onClick,
  className = "",
  children,
  showTooltip = false,
  forceExpanded = false,
}: SidebarMenuButtonProps) {
  const { state, isMobile, setOpen } = useSidebar();
  const isCollapsed = state === "collapsed" && !forceExpanded;

  // Handle click: close sidebar on mobile and call original onClick
  const handleClick = () => {
    if (isMobile) {
      setOpen(false);
    }
    onClick?.();
  };

  // Base styles for the button - keep icon position fixed
  const baseStyles = `
    group flex items-center h-3xl w-full
    rounded-md transition-all duration-50
    cursor-pointer no-underline
    font-medium text-sm leading-5
    gap-sm px-sm py-xs
  `;

  // Active/inactive styles
  const stateStyles = isActive
    ? "bg-brand-lightest text-brand font-bold"
    : "text-secondary hover:bg-tertiary hover:text-primary";

  // Icon container styles - consistent size to prevent shifting
  const iconStyles = "w-xl h-xl shrink-0 flex items-center justify-center";

  // Label styles - collapse to zero width smoothly
  const labelStyles = isCollapsed
    ? "w-0 min-w-0 opacity-0 overflow-hidden transition-[width,min-width,opacity] duration-300"
    : "overflow-hidden text-ellipsis whitespace-nowrap transition-[width,min-width,opacity] duration-300";

  // Check if current route matches this link (must be at top level, before any conditionals)
  const match = useMatch({ path: to ? `${to}/*` : "/", end: false });

  const buttonClasses = `${baseStyles} ${stateStyles} ${className}`.trim();

  const tooltipIdRef = useRef(`sidebar-btn-tooltip-${label}`);
  const tooltipEnabled = showTooltip && isCollapsed && !!label;
  const tooltipProps = tooltipEnabled
    ? {
        "data-tip": label,
        "data-for": tooltipIdRef.current,
        "data-event": "mouseenter" as const,
        "data-event-off": "mouseleave" as const,
      }
    : {};

  // Render custom children with asChild pattern
  if (asChild && children) {
    return React.cloneElement(children as React.ReactElement, {
      className: buttonClasses,
    });
  }

  // Render with React Router NavLink for internal navigation
  if (to) {
    // isActive === false → force inactive; isActive === true → force active; undefined → URL match
    const isLinkActive =
      isActive === false ? false : !!match || isActive === true;

    const navLinkBaseClasses =
      `${baseStyles} text-secondary hover:bg-brand-lightest hover:text-brand ${className}`.trim();
    return (
      <>
        <NavLink
          to={to}
          className={isLinkActive
            ? `${navLinkBaseClasses} !bg-brand-lightest !text-brand !font-bold is-active`
            : navLinkBaseClasses
          }
          onClick={handleClick}
          {...tooltipProps}
        >
          {icon && (
            <span className={`${iconStyles} group-hover:hidden group-[.is-active]:hidden`}>
              {React.isValidElement(icon) ? (
                <MakulaIcon icon={icon} className="w-xl h-xl" />
              ) : (
                icon
              )}
            </span>
          )}
          {activeIcon && (
            <span className={`${iconStyles} hidden group-hover:block group-[.is-active]:block`}>
              {React.isValidElement(activeIcon) ? (
                <MakulaIcon icon={activeIcon} className="w-xl h-xl" />
              ) : (
                activeIcon
              )}
            </span>
          )}
          {label && <span className={labelStyles}>{label}</span>}
          {children}
        </NavLink>
        {tooltipEnabled && <Tooltip id={tooltipIdRef.current} place="right" />}
      </>
    );
  }

  const nonLinkClasses = `${baseStyles} ${isActive ? "bg-brand-lightest text-brand font-bold" : "text-secondary hover:bg-brand-lightest hover:text-brand"} ${className}`.trim();

  // Render as anchor for external links
  if (href) {
    const anchorClasses = isActive
      ? `${nonLinkClasses} is-active`
      : nonLinkClasses;
    return (
      <>
        <a
          href={href}
          className={anchorClasses}
          onClick={handleClick}
          {...tooltipProps}
        >
          {icon && (
            <span className={`${iconStyles} group-hover:hidden group-[.is-active]:hidden`}>
              {React.isValidElement(icon) ? (
                <MakulaIcon icon={icon} className="w-xl h-xl" />
              ) : (
                icon
              )}
            </span>
          )}
          {activeIcon && (
            <span className={`${iconStyles} hidden group-hover:block group-[.is-active]:block`}>
              {React.isValidElement(activeIcon) ? (
                <MakulaIcon icon={activeIcon} className="w-xl h-xl" />
              ) : (
                activeIcon
              )}
            </span>
          )}
          {label && <span className={labelStyles}>{label}</span>}
          {children}
        </a>
        {tooltipEnabled && <Tooltip id={tooltipIdRef.current} place="right" />}
      </>
    );
  }

  // Render as button
  const buttonElementClasses = isActive
    ? `${nonLinkClasses} is-active`
    : nonLinkClasses;
  return (
    <>
      <button
        type="button"
        className={buttonElementClasses}
        onClick={handleClick}
        {...tooltipProps}
      >
        {icon && (
          <span className={`${iconStyles} group-hover:hidden group-[.is-active]:hidden`}>
            {React.isValidElement(icon) ? (
              <MakulaIcon icon={icon} className="w-xl h-xl" />
            ) : (
              icon
            )}
          </span>
        )}
        {activeIcon && (
          <span className={`${iconStyles} hidden group-hover:block group-[.is-active]:block`}>
            {React.isValidElement(activeIcon) ? (
              <MakulaIcon icon={activeIcon} className="w-xl h-xl" />
            ) : (
              activeIcon
            )}
          </span>
        )}
        {label && <span className={labelStyles}>{label}</span>}
        {children}
      </button>
      {tooltipEnabled && <Tooltip id={tooltipIdRef.current} place="right" />}
    </>
  );
}
